import { getFormattedHoursFromMins, getMinutesFromFormattedHours } from "./function";

/**
 * The rules about a day's recorded hours.
 *
 * These same rules live in the technician app (src/common/hours.js there). The
 * two are separate deployables and cannot import from one another, so this is a
 * deliberate copy rather than an accident — but it is a copy, and the two must
 * be changed together. What they must agree on is not cosmetic: whether a day
 * overlaps, and how long a span is, decides what a person is paid.
 */

// The two worked spans an entry can carry. Lunch is not among them on purpose:
// it is meant to sit inside the working hours, so it overlaps them by
// definition.
export const SPAN_FIELDS = [
  ["regularStart", "regularStop", "work hours"],
  ["travelStart", "travelStop", "travel time"],
];

export const isClock = (value) => typeof value === "string" && /^\d{2}:\d{2}$/.test(value);

export const clockToMinutes = (value) => getMinutesFromFormattedHours(value);

/**
 * Elapsed time between two clock times as an "HH:MM" duration. A stop before the
 * start means the shift ran past midnight and belongs to the next day; without
 * that, 10:00 PM - 2:00 AM reads as negative and collapses to 00:00.
 */
export const durationBetween = (start, stop) => {
  if (!start || !stop) return null;
  const from = clockToMinutes(start);
  const to = clockToMinutes(stop);
  return getFormattedHoursFromMins(to >= from ? to - from : to + 24 * 60 - from);
};

/**
 * A start/stop pair as a position and a length, or null when it is not a usable
 * span. The midnight rule matches durationBetween exactly, because a span that
 * is four hours long for pay has to be four hours long for these checks too.
 * Equal start and stop is zero-length and cannot overlap.
 */
export const spanBetween = (start, stop) => {
  if (!isClock(start) || !isClock(stop)) return null;
  const from = clockToMinutes(start);
  const to = clockToMinutes(stop);
  if (to === from) return null;
  return { from, duration: to > from ? to - from : to + 24 * 60 - from };
};

/**
 * Compared as arcs on a 24-hour clock rather than as points on a timeline.
 * These are clock times with no date attached, so 01:00 is genuinely ambiguous
 * — the morning before an evening shift, or the small hours after it. Testing
 * the second arc a day either side settles it without needing to know the date.
 */
export const arcsOverlap = (a, b) => {
  for (const shift of [-24 * 60, 0, 24 * 60]) {
    const from = b.from + shift;
    if (a.from < from + b.duration && from < a.from + a.duration) return true;
  }
  return false;
};

// A span whose ends are the same records no time at all. Worth catching rather
// than ignoring: durationBetween returns the string "00:00", which is truthy, so
// such a span saves happily as an entry standing for nothing.
export const isZeroSpan = (start, stop) =>
  isClock(start) && isClock(stop) && clockToMinutes(start) === clockToMinutes(stop);

export const workedSpans = (entries, label) =>
  entries.flatMap((entry, index) =>
    SPAN_FIELDS.flatMap(([startKey, stopKey, field]) => {
      const span = spanBetween(entry[startKey], entry[stopKey]);
      if (!span) return [];
      return [{ index, describe: label(index, field, entry[startKey], entry[stopKey]), ...span }];
    })
  );

/**
 * Pairs of spans covering the same clock time. Touching ends (12:00 stop, 12:00
 * start) are not an overlap — that is the normal way a second stretch follows
 * the first.
 */
export const overlapConflicts = (entries, label, external = []) => {
  const spans = [...workedSpans(entries, label), ...external];
  const found = [];
  for (let i = 0; i < spans.length; i++) {
    for (let j = i + 1; j < spans.length; j++) {
      if (spans[i].index === null && spans[j].index === null) continue;
      if (arcsOverlap(spans[i], spans[j])) found.push([spans[i], spans[j]]);
    }
  }
  return found;
};

// Spans that start and stop at the same moment. Half-filled spans are not
// included: one end missing is an entry still being typed, not a claim that no
// time passed.
export const emptySpans = (entries, label) =>
  entries.flatMap((entry, index) =>
    SPAN_FIELDS.flatMap(([startKey, stopKey, field]) =>
      isZeroSpan(entry[startKey], entry[stopKey])
        ? [{ index, describe: label(index, field, entry[startKey]) }]
        : []
    )
  );

// An entry nobody filled in. Storing one would leave a blank stretch in the day.
export const entryHasTime = (entry) =>
  Boolean(
    entry &&
      (entry.regularTime ||
        entry.travelTime ||
        entry.overTime ||
        entry.regularStart ||
        entry.regularStop ||
        entry.travelStart ||
        entry.travelStop)
  );

// What a stretch holds, so a confirmation can name it rather than asking about
// "this entry" and leaving the reader to work out which hours.
export const describeEntry = (entry) => {
  const parts = [];
  const regular = durationBetween(entry.regularStart, entry.regularStop) ?? entry.regularTime;
  const travel = durationBetween(entry.travelStart, entry.travelStop) ?? entry.travelTime;
  if (regular && regular !== "00:00") parts.push(`${regular} regular`);
  if (travel && travel !== "00:00") parts.push(`${travel} travel`);
  if (entry.overTime && entry.overTime !== "00:00") parts.push(`${entry.overTime} overtime`);
  return parts.join(", ");
};

export const labelEntrySpan = (index, field, start, stop) =>
  `entry ${index + 1} ${field} (${start}–${stop})`;

export const labelZeroSpan = (index, field, start) =>
  `entry ${index + 1} ${field} starts and ends at ${start}`;

// Sum a day's "HH:MM" figures. Returns "" when nothing carries a value, so an
// untouched field stays untouched.
export const addTimeValues = (values) => {
  let minutes = 0;
  let sawValue = false;
  for (const value of values) {
    if (!isClock(value)) continue;
    minutes += clockToMinutes(value);
    sawValue = true;
  }
  return sawValue ? getFormattedHoursFromMins(minutes) : "";
};
